// web/src/api/client.ts — הדיבור עם השרת (D-245).
// 🔑 כל נתיב וכל שדה מוקלדים מהחוזים (src/api/gen — נוצר ע"י scripts/sync.py). שינוי בחוזה ⇒ הבנייה נשברת,
//    ⛔ לא תקלה שקטה מול לקוחה.
// 🔑 האסימון מצורף לכל בקשה אוטומטית, מהכניסה הנוכחית של Supabase (מתחדש לבד).
import createClient, { type Middleware } from "openapi-fetch";
import type { paths as CorePaths } from "./gen/core";
import type { paths as CaPaths } from "./gen/ca";
import type { paths as CrPaths } from "./gen/cr";
import type { paths as StPaths } from "./gen/st";
import type { paths as LrPaths } from "./gen/lr";
import type { paths as AcPaths } from "./gen/ac";
import type { paths as MsPaths } from "./gen/ms";
import type { paths as DkPaths } from "./gen/dk";
import type { paths as TfPaths } from "./gen/tf";
import type { paths as AfPaths } from "./gen/af";
import type { paths as LpPaths } from "./gen/lp";
import type { paths as DvPaths } from "./gen/dv";
import type { paths as OvPaths } from "./gen/ov";
import type { paths as WpPaths } from "./gen/wp";
import type { paths as HmPaths } from "./gen/hm";
import { config } from "../lib/config";
import { supabase } from "../lib/supabase";

const withToken: Middleware = {
  async onRequest({ request }) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
};

function make<P extends {}>() {
  const c = createClient<P>({ baseUrl: config.apiUrl });
  c.use(withToken);
  return c;
}

export const core = make<CorePaths>();
export const ca = make<CaPaths>();
export const cr = make<CrPaths>();
export const st = make<StPaths>();                  // 🆕 27.09 — הסטודיו (ST)
export const lr = make<LrPaths>();                  // 🆕 27.09 — מרחב הלמידה (LR)
export const ac = make<AcPaths>();                  // 🆕 28.09 — האזור האישי (AC)
export const ms = make<MsPaths>();                  // 🆕 28.09 — תיבת ההודעות (MS) — פעימה 1
export const dk = make<DkPaths>();                  // 🆕 28.09 — השולחן של עתידה + הערה לשיפור
export const tf = make<TfPaths>();
export const lp = make<LpPaths>();                  // 🆕 01.10 — מסלולי למידה (LP · הגירה 100)
export const dv = make<DvPaths>();                  // 🆕 03.10 — דיוור: הגדרות · נוסחים · התור (ב1)
export const ov = make<OvPaths>();                  // 🆕 03.10 — מה קורה בעסק (ב7)
export const hm = make<HmPaths>();                  // 🆕 06.10 — איסוף המלצות (HM · 140)
export const wp = make<WpPaths>();                  // 🆕 04.10 — מוצרים ומלאי (WP-05)
export const af = make<AfPaths>();                  // 🆕 01.10 — שותפים (AF) — פעימה א                  // 🆕 29.09 — תפארת, העוזרת של הצוות

/** 🆕 29.09 — בקשה עם האסימון שאינה JSON (הקלטות של תפארת). ⛔ מחזירה null בכל כשל — הטקסט תמיד על המסך. */
export async function fetchBlob(path: string): Promise<Blob | null> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    const r = await fetch(`${config.apiUrl}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    return r.ok ? await r.blob() : null;
  } catch {
    return null;
  }
}

/** 🆕 29.09 לילה — POST שמחזיר קובץ (הקול של תשובה של תפארת). כשל = null. */
export async function postBlob(path: string, body: unknown): Promise<Blob | null> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    const r = await fetch(`${config.apiUrl}${path}`, {
      method: "POST", body: JSON.stringify(body),
      headers: { "content-type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    return r.ok ? await r.blob() : null;
  } catch {
    return null;
  }
}

/** השרת מחזיר {detail: {code, message}} לשגיאה עסקית, או {detail: "..."} לשגיאה כללית.
 *  🔑 ההודעה בעברית באה מהשרת — ⛔ החזית לא ממציאה ניסוח משלה. */
export type ApiError = { status: number; code: string | null; message: string };

export function toApiError(status: number, body: unknown): ApiError {
  const d = (body as { detail?: unknown } | null)?.detail;
  if (d && typeof d === "object" && "message" in d) {
    const o = d as { code?: string; message: string };
    return { status, code: o.code ?? null, message: o.message };
  }
  if (typeof d === "string") return { status, code: d, message: GENERIC[status] ?? d };
  return { status, code: null, message: GENERIC[status] ?? "משהו השתבש. אפשר לנסות שוב." };
}

const GENERIC: Record<number, string> = {
  401: "צריך להיכנס מחדש.",
  403: "אין לך הרשאה לפעולה הזו.",
  404: "לא נמצא — אולי זה כבר השתנה.",
};
